import { ExternalLink, ImageOff } from 'lucide-react';
import type { InboxThreadView } from '../../types/inboxView';
import { CHANNEL_LABEL, isProbablyVideoUrl } from '../../lib/inboxView';
import { ChannelIcon } from '../../components/ui/ChannelIcon';

// Community Inbox — the LARGE post preview (post-preview-fix §2). The post is
// the context for the whole thread, so at full width it gets a persistent
// right-hand panel (media shown big + caption underneath + comment count +
// "View on <platform>") instead of a cramped header strip. Video URLs render
// a playable <video>; media-less posts get a placeholder + the permalink.
// On narrower viewports the compact header strip renders instead (caller
// controls visibility with responsive classes).

function PostMedia({
  url,
  permalink,
  channelLabel,
}: {
  url: string | null;
  permalink: string | null;
  channelLabel: string;
}) {
  if (!url) {
    // A1 (2026-07-19) — an honest empty state instead of a broken-image
    // placeholder. Post copy/media come from the roster-wide list row, which the
    // background sweep backfills, so a freshly-arrived (webhook-created) thread
    // legitimately has none yet. Say so, and always offer the permalink.
    return (
      <div className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 text-center text-slate-400">
        <ImageOff className="h-8 w-8" />
        <span className="text-xs">Post preview not synced yet</span>
        <span className="text-[11px] leading-snug">
          {channelLabel} post media fills in on the next sync.
        </span>
        {permalink && (
          <a
            href={permalink}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-kyck-700 hover:underline"
          >
            View on {channelLabel}
          </a>
        )}
      </div>
    );
  }
  if (isProbablyVideoUrl(url)) {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        src={url}
        controls
        muted
        playsInline
        preload="metadata"
        className="max-h-[60vh] w-full rounded-md bg-black object-contain"
      />
    );
  }
  return (
    <img
      src={url}
      alt="Post media"
      loading="lazy"
      className="max-h-[60vh] w-full rounded-md bg-slate-100 object-contain"
    />
  );
}

export function PostPreviewPanel({
  thread,
  className,
}: {
  thread: InboxThreadView;
  className?: string;
}) {
  const postLink = thread.postPermalink ?? thread.permalink ?? null;
  return (
    <div className={className}>
      <div className="scroll-subtle flex h-full w-full flex-col overflow-y-auto border-l border-slate-200 bg-slate-50/50 p-4">
        <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
          <ChannelIcon channel={thread.channel} className="h-3.5 w-3.5" />
          Post
        </div>
        <PostMedia
          url={thread.postImageUrl ?? null}
          permalink={postLink}
          channelLabel={CHANNEL_LABEL[thread.channel]}
        />
        {thread.postContent ? (
          <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
            {thread.postContent}
          </p>
        ) : (
          <p className="mt-3 text-sm italic text-slate-400">
            Post copy not synced yet — it fills in on the next sync.
          </p>
        )}
        <div className="mt-3 flex items-center gap-3 border-t border-slate-200 pt-3 text-xs text-slate-500">
          {typeof thread.postLikeCount === 'number' && (
            <span title="Likes/reactions as of the last sync (not live)">
              {thread.postLikeCount} like{thread.postLikeCount === 1 ? '' : 's'}
            </span>
          )}
          {typeof thread.postCommentCount === 'number' && (
            <span>
              {thread.postCommentCount} comment{thread.postCommentCount === 1 ? '' : 's'}
            </span>
          )}
          {postLink && (
            <a
              href={postLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-kyck-700 hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              View on {CHANNEL_LABEL[thread.channel]}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// The compact strip for narrow panes (the large panel above carries the post at width).
// Moved here from ThreadDetail.tsx so the club portal shares it.
export function PostContextHeader({ thread }: { thread: InboxThreadView }) {
  const postLink = thread.postPermalink ?? thread.permalink;
  const hasContext = Boolean(thread.postContent || thread.postImageUrl || postLink);
  if (!hasContext) return null;
  return (
    <div className="flex items-start gap-3 border-b border-slate-200 bg-slate-50/70 px-4 py-3">
      {thread.postImageUrl && (
        <img
          src={thread.postImageUrl}
          alt="Post"
          className="h-16 w-16 shrink-0 rounded-md object-cover"
        />
      )}
      <div className="min-w-0 flex-1">
        {thread.postContent ? (
          <p className="line-clamp-3 whitespace-pre-wrap text-sm text-slate-700" title={thread.postContent}>
            {thread.postContent}
          </p>
        ) : (
          <p className="text-sm italic text-slate-400">Post content unavailable</p>
        )}
        <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
          {typeof thread.postLikeCount === 'number' && (
            <span title="Likes/reactions as of the last sync (not live)">
              {thread.postLikeCount} like{thread.postLikeCount === 1 ? '' : 's'}
            </span>
          )}
          {typeof thread.postCommentCount === 'number' && (
            <span>
              {thread.postCommentCount} comment{thread.postCommentCount === 1 ? '' : 's'}
            </span>
          )}
          {postLink && (
            <a
              href={postLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-kyck-700 hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              View on {CHANNEL_LABEL[thread.channel]}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
